// ** React Imports
import { useRef, useState, useEffect, useCallback } from "react";

// ** Icons
import ChevronLeft from "mdi-material-ui/ChevronLeft";
import ChevronRight from "mdi-material-ui/ChevronRight";

// ** Components
import FlixCard from "src/flix/components/FlixCard";

/******************************************************************
 * ##: FlixRow
 * Horizontal "rail" of course cards (Netflix-style row). A CSS
 * scroll-snap track (no carousel dependency) with overlaid chevron
 * buttons for manual paging; the native scrollbar is hidden and the
 * next card "peeks" at the edge. Arrows only render when there is more
 * to scroll in that direction. Renders nothing when empty (unless
 * loading, where it shows skeletons).
 * @param {Object} props - { title, courses, loading }
 * @returns {JSX.Element|null} - the `.flix-row`
 * History:
 * 22-07-2026: Created
 * 22-07-2026: Netflix-style peek + manual chevron paging
 ******************************************************************/
const FlixRow = ({ title, courses = [], loading = false, variant }) => {
  const trackRef = useRef(null);
  const scrollHideTimer = useRef(null);
  const dragState = useRef({ active: false, startX: 0, startScroll: 0, moved: false });
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, clientWidth, scrollWidth } = el;
    setCanLeft(scrollLeft > 4);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [updateArrows, courses, loading]);

  // Reveal the (themed) scrollbar only while scrolling, then hide it again.
  const handleTrackScroll = () => {
    updateArrows();
    const el = trackRef.current;
    if (!el) return;
    el.classList.add("is-scrolling");
    if (scrollHideTimer.current) clearTimeout(scrollHideTimer.current);
    scrollHideTimer.current = setTimeout(() => {
      if (trackRef.current) trackRef.current.classList.remove("is-scrolling");
    }, 700);
  };

  useEffect(() => {
    return () => {
      if (scrollHideTimer.current) clearTimeout(scrollHideTimer.current);
    };
  }, []);

  const scrollByPage = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.85, behavior: "smooth" });
  };

  // Drag-to-scroll with the mouse (touch/pen keep native scrolling). A drag
  // sets `moved`, which suppresses the card's click so it does not navigate.
  const onPointerDown = (e) => {
    if (e.pointerType !== "mouse") return;
    const el = trackRef.current;
    if (!el) return;
    dragState.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
    el.classList.add("is-dragging");
  };

  const onPointerMove = (e) => {
    const state = dragState.current;
    if (!state.active) return;
    const el = trackRef.current;
    if (!el) return;
    const dx = e.clientX - state.startX;
    if (Math.abs(dx) > 4) state.moved = true;
    el.scrollLeft = state.startScroll - dx;
  };

  const onPointerEnd = () => {
    const state = dragState.current;
    if (!state.active) return;
    state.active = false;
    if (trackRef.current) trackRef.current.classList.remove("is-dragging");
  };

  const onClickCapture = (e) => {
    if (dragState.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      dragState.current.moved = false;
    }
  };

  if (!loading && (!courses || courses.length === 0)) return null;

  return (
    <section className="flix-row">
      <h2 className="flix-row__title">{title}</h2>
      <div className="flix-row__viewport">
        {canLeft && (
          <button type="button" className="flix-row__nav flix-row__nav--left" onClick={() => scrollByPage(-1)} aria-label="Anterior">
            <ChevronLeft />
          </button>
        )}

        <div
          className="flix-row__track"
          ref={trackRef}
          onScroll={handleTrackScroll}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onPointerLeave={onPointerEnd}
          onClickCapture={onClickCapture}
        >
          {loading
            ? Array.apply(null, { length: 6 }).map((e, i) => <FlixCard key={i} loading />)
            : courses.map((course, i) => <FlixCard key={course.flix_module_id ?? course.course_id ?? i} data={course} variant={variant} />)}
        </div>

        {canRight && (
          <button type="button" className="flix-row__nav flix-row__nav--right" onClick={() => scrollByPage(1)} aria-label="Seguinte">
            <ChevronRight />
          </button>
        )}
      </div>
    </section>
  );
};

export default FlixRow;
